import React from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatDate } from 'date-fns';
import { groupBy } from 'lodash';

import EnrichmentActivityChart from '../charts/EnrichmentActivityChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { KeyStudentEngagementEnrichmentActivityResult } from 'types/schema-generated-types';

const StudentEnrichmentActivityChart: React.FC<{
  reportData?: KeyStudentEngagementEnrichmentActivityResult[];
  dateIntervals: string[];
  dateIntervalFormat: string;
  loading: boolean;
}> = ({ reportData = [], dateIntervals, dateIntervalFormat, loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts']);

  const intervalValues = reportData.reduce(
    (acc: { date: string; template: string }[], { dateCompleted, aborted, template }) => {
      if (!dateCompleted || aborted) return acc;
      return [...acc, { date: formatDate(new Date(dateCompleted), dateIntervalFormat), template }];
    },
    []
  );

  const intervalValuesByDate = groupBy(intervalValues, 'date');
  const {
    hearItTypeItCompletedSeries,
    myChoiceKeyboardingCompletedSeries,
    teacherCreatedActivityCompletedSeries
  } = dateIntervals.reduce(
    (acc, date) => {
      const dateIntervalValues = intervalValuesByDate[date];
      if (!dateIntervalValues) {
        acc.hearItTypeItCompletedSeries.push(0);
        acc.myChoiceKeyboardingCompletedSeries.push(0);
        acc.teacherCreatedActivityCompletedSeries.push(0);
      } else {
        acc.hearItTypeItCompletedSeries.push(
          dateIntervalValues.filter(d => d.template === 'HearItTypeIt').length
        );
        acc.myChoiceKeyboardingCompletedSeries.push(
          dateIntervalValues.filter(d => d.template === 'MyChoiceKeyboarding').length
        );
        acc.teacherCreatedActivityCompletedSeries.push(
          dateIntervalValues.filter(d => d.template === 'TeacherCreation').length
        );
      }

      return acc;
    },
    {
      hearItTypeItCompletedSeries: [] as number[],
      myChoiceKeyboardingCompletedSeries: [] as number[],
      teacherCreatedActivityCompletedSeries: [] as number[]
    }
  );

  return (
    <ReportCard
      title={t('Enrichment Activities')}
      helpText={t('enrichmentActivities', { ns: 'ReportsHelpCharts' })}
      loading={loading}
      reportData={reportData}
    >
      <EnrichmentActivityChart
        hearItTypeItCompletedSeries={hearItTypeItCompletedSeries}
        myChoiceKeyboardingCompletedSeries={myChoiceKeyboardingCompletedSeries}
        teacherCreatedActivityCompletedSeries={teacherCreatedActivityCompletedSeries}
        labels={dateIntervals}
      />
    </ReportCard>
  );
};

export default StudentEnrichmentActivityChart;
